import React, { useEffect, useState } from 'react';

import { PanelsEnum, Resources } from '@ariksa/inventory-core/api';
import { useDisclosure } from '@chakra-ui/react';
import { filter } from 'lodash';
import { useSelector } from 'react-redux';

import { formatNumberWithComma, getIconColor } from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { DeployOutpostForDataMessage } from 'containers/Dashboard/utils/DeployOutpostForDataMessage';
import { renderResourcesWithIconAndCount } from 'containers/Dashboard/utils/utils';
import { getContainerCount } from 'containers/Dashboard/VulnerabilityOverview/Components/utils';
import { selectVulnerabilityOverview } from 'containers/Dashboard/VulnerabilityOverview/selectors';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {
  scannerDeployed: boolean;
}

export const VulnerableHostsWithAccessToSensitiveData: React.FC<Props> = props => {
  const { vulnerableEntitiesWithSensitiveDataAccess } = useSelector(
    selectVulnerabilityOverview,
  );
  const { data, isLoading } = vulnerableEntitiesWithSensitiveDataAccess;
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const { isDataScannerDeployed } = useSelector(selectSharedState);
  const { environmentId } = useAccessBoundary();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setTotal(
      (data?.items?.['Virtual Machine'] ?? 0) +
        (data?.items?.['Serverless'] ?? 0) +
        getContainerCount(data?.items) +
        (data?.items?.['Kubernetes'] ?? 0),
    );
  }, [data]);

  return (
    <>
      <DashboardWidget
        label="Vulnerable Hosts with Sensitive Data Access"
        isLoading={isLoading}
        // Show the empty state when the scanner is deployed but every host count is zero
        noData={!!isDataScannerDeployed?.[environmentId!] && !total}
        styles={{ contentWrapper: { px: 0 } }}
        subHeader={
          formatNumberWithComma(total) +
          ' vulnerable host' +
          (total === 1 ? '' : 's')
        }
        content={
          isDataScannerDeployed?.[environmentId!] ? (
            // Drop host types with a zero count so only affected hosts are listed
            renderResourcesWithIconAndCount(
              filter(
                [
                  {
                    label: 'Virtual Machine',
                    count: (data?.items?.['Virtual Machine'] as number) || 0,
                    isLoading: isLoading,
                    iconType: IconTypes.Ec2Instance,
                    iconColor: getIconColor(IconTypes.Ec2Instance),
                    onClick: () => {
                      allEntitiesDisclosure.onOpen();
                      setDetails({
                        panel: PanelsEnum.VulnerableWithAccessToSensitive,
                        iconType: IconTypes.VirtualMachine,
                        // Use the Virtual Machine count (was copy-pasted from Containers)
                        total:
                          (data?.items?.['Virtual Machine'] as number) || 0,
                        resourceType: Resources.VirtualMachine,
                        resourceTypeClass: 'agnostic',
                      });
                    },
                  },
                  {
                    label: 'Serverless',
                    count: (data?.items?.['Serverless'] as number) || 0,
                    isLoading: isLoading,
                    iconType: IconTypes.LambdaFunction,
                    iconColor: getIconColor(IconTypes.LambdaFunction),
                    onClick: () => {
                      allEntitiesDisclosure.onOpen();
                      setDetails({
                        panel: PanelsEnum.VulnerableWithAccessToSensitive,
                        iconType: IconTypes.LambdaFunction,
                        total: (data?.items?.['Serverless'] as number) || 0,
                        resourceType: Resources.ServerlessCompute,
                        resourceTypeClass: 'agnostic',
                      });
                    },
                  },
                  {
                    label: 'Containers',
                    // Sum both container keys (previously picked only one).
                    count: getContainerCount(data?.items),
                    isLoading: isLoading,
                    iconType: IconTypes.Container,
                    iconColor: getIconColor(IconTypes.Container),
                    onClick: () => {
                      allEntitiesDisclosure.onOpen();
                      setDetails({
                        panel: PanelsEnum.VulnerableWithAccessToSensitive,
                        iconType: IconTypes.Container,
                        // Drawer total previously ignored 'Container'; match the row count.
                        total: getContainerCount(data?.items),
                        resourceType: Resources.Containers,
                        resourceTypeClass: 'agnostic',
                      });
                    },
                  },
                  {
                    label: 'Kubernetes',
                    count: (data?.items?.['Kubernetes'] as number) || 0,
                    isLoading: isLoading,
                    iconType: IconTypes.Kubernetes,
                    iconColor: getIconColor(IconTypes.Kubernetes),
                    onClick: () => {
                      allEntitiesDisclosure.onOpen();
                      setDetails({
                        panel: PanelsEnum.VulnerableWithAccessToSensitive,
                        iconType: IconTypes.Kubernetes,
                        total: (data?.items?.['Kubernetes'] as number) || 0,
                        resourceType: Resources.Kubernetes,
                        resourceTypeClass: 'agnostic',
                      });
                    },
                  },
                ],
                o => !!o.count,
              ),
            )
          ) : (
            <DeployOutpostForDataMessage />
          )
        }
        /*contentTooltip={
          props.scannerDeployed ? undefined : (
            <Center w="full">
              <Box w="60%">
                <DashboardOverviewTooltip
                  button={
                    <Center w="full">
                      <HStack spacing={4} align="center">
                        <Box boxSize={5} color="primary">
                          <Center>
                            <IdeaGlitterIcon />
                          </Center>
                        </Box>
                        <Box color="primary" fontSize="md" flex={1}>
                          Deploy Outpost for Data to identify risks to sensitive
                          data from vulnerable entities
                        </Box>
                      </HStack>
                    </Center>
                  }
                  header="Deploy Ariksa Outpost for Vulnerability"
                  tooltip={
                    <Stack>
                      <Box>
                        Ariksa Outpost for Vulnerability detects vulnerabilities
                        and exploits in OS, application, libraries and databases
                        that are hosted within virtual machine and containers.
                      </Box>
                      <Box>
                        Ariksa automatically connects hidden dots to uncover
                        attack paths and risks to data arising out of a
                        combination of exploitable vulnerabilities Internet
                        exposure, privileged roles, leaked credentials and data
                        sensitivity, to help you drive efficient prioritization
                        of your security efforts.
                      </Box>
                    </Stack>
                  }
                  footerText="Deploy now"
                  onClickFooter={() =>
                    navigate('/setup/outpost/add/vulnerability')
                  }
                />
              </Box>
            </Center>
          )
        }*/
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        vulnerable
        {...details}
      />
    </>
  );
};
